<template>
	<div
		class="quest-box"
		v-if="quest"
		:class="{ completed: questCompleted && !questActionRequired }"
	>
		<div v-if="isPopup" class="quest-box-img" />

		<div class="quest-box__header">
			<BoxHeaderBackground slug="quest-box-header" />
			<QuestBoxHeader
				:type="quest.type"
				:title="translate(quest.title)"
				:show-pin="!isPopup"
				:quest-id="quest.id"
				@pin-action="pinAction"
			/>
		</div>
		<!-- TODO podmienku potom napojit na spravny stav questu - pre otestovanie popupu dat `false` namiesto `quest` -->
		<div v-if="quest || isPopup" class="quest-box__content">
			<span class="quest-box-description">
				{{ quest.flavor.length > 0 ? translate(quest.flavor) : "" }}
			</span>

			<QuestTask
				v-if="quest"
				:current="quest.progress"
				:target="getAmount(quest.objective.amount)"
				:building="hasType(quest.objective) ? quest.objective.type : ''"
				:text="translate(quest.assignment)"
				:hint="quest.hintText ? translate(quest.hintText) : ''"
				:building-level="
					hasLevel(quest.objective) ? quest.objective.level : 0
				"
				:quest-action-required="questActionRequired"
				:town="getTownName"
				:icon="hasType(quest.objective) ? quest.objective.type : ''"
			/>
			<slot />
			<QuestBoxRewards
				:quest="quest"
				:questCompleted="questCompleted && !questActionRequired"
			/>
			<AppTextButton
				v-if="questActionRequired"
				class="claim-rewards-btn"
				:type="StatusTypes.PRIMARY"
				:text="claimRewardsBtnActionText"
				@click="payForQuest"
			/>
			<AppTextButton
				v-if="claimRewardButtonShow && !isPopup"
				class="claim-rewards-btn"
				:type="StatusTypes.SUCCESS"
				:text="translate('button.claim')"
				:pulse="true"
				@click="handleClaimRewards"
			/>
		</div>
		<AppTextButton
			v-else
			class="claim-rewards-btn"
			:type="StatusTypes.PRIMARY"
			:text="translate('button.accept')"
			@click="startQuest"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import QuestBoxHeader from "@/components/Quests/QuestBoxHeader.vue";
import QuestBoxRewards from "@/components/Quests/QuestBoxRewards.vue";
import QuestTask from "@/components/Quests/QuestTask.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import QuestRepository, {
	hasLevel,
	hasSettlementId,
	hasType,
} from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import type { QuestActive, UseResourcesObjective } from "@/types/quest";
import { type QuestPinStatus } from "@/types/quest";
import { StatusTypes } from "@/types/ui";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";

interface Props {
	quest: QuestActive | null;
	isPopup?: boolean;
}

const emit = defineEmits(["action", "claimRewards", "pinAction"]);

const { quest = null, isPopup = false } = defineProps<Props>();

const soundService = markRaw(SoundService.getInstance());
const questrepo = markRaw(QuestRepository.getInstance());

const resourcesStore = useResourcesStore();
const questStore = useQuestStore();
const { trackedQuestId } = storeToRefs(questStore);

const questCompleted = computed((): boolean => {
	if (!quest) {
		return false;
	}
	return quest.progress >= getAmount(quest.objective.amount);
});

const claimRewardButtonShow = computed((): boolean => {
	if (!quest) {
		return false;
	}

	if (quest?.payed === 0) {
		return false;
	} else if (quest?.payed === 1) {
		return true;
	}

	return questCompleted.value;
});

const handleClaimRewards = (): void => {
	emit("claimRewards", quest?.id);
};

const questActionRequired = computed((): boolean => {
	return (
		quest?.objective.action === QuestObjectiveActions.USE_RESOURCES &&
		quest.progress >= getAmount(quest.objective.amount) &&
		quest?.payed === 0
	);
});

const getTownName = computed((): string => {
	if (!quest || !quest.objective || !hasSettlementId(quest.objective)) {
		return "";
	}

	const actualSettlement = settlementNames.get(quest.objective.settlement_id);
	if (!actualSettlement) {
		return "";
	}

	return actualSettlement.name;
});

const payForQuest = (): void => {
	if (!quest || !quest.objective || !hasType(quest.objective)) return;

	const resource = quest.objective.type as unknown as ResourceType;
	resourcesStore.decrease(
		resource,
		getAmount(quest.objective.amount),
		"quest: price",
	);
	questrepo.manageQuestState(
		QuestObjectiveActions.USE_RESOURCES,
		null,
		getAmount(quest.objective.amount),
	);

	soundService.playGenerickClick();
};

const startQuest = (): void => {
	emit("action");

	soundService.playGenerickClick();
};

const claimRewardsBtnActionText = computed((): string => {
	if ((quest?.objective as UseResourcesObjective)?.type === ResourceType.ARMY)
		return translate("button.deploy");
	return translate("button.pay");
});

const pinAction = (status: { pinStatus: QuestPinStatus }): void => {
	if (trackedQuestId.value !== quest?.id) {
		questStore.setTrackedQuest(quest?.id ?? null);
	} else {
		questStore.setTrackedQuest(null);
	}
	emit("pinAction", status);
};
</script>

<style lang="scss" scoped>
.quest-box {
	position: relative;
	gap: 0.5rem;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #836e49, #bfa370);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #ab8849, #f2dcb5);
	width: 100%;
	max-width: 25rem;
	height: auto;
	&.completed {
		box-shadow:
			0 0 0.5rem 0 rgba(43, 173, 80, 0.8),
			inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border: solid 0.05rem #2bad50;
	}

	span {
		color: #372615 !important;
		font-size: 0.813rem;
		text-shadow: none !important;
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 1;
		mix-blend-mode: multiply;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&__content {
		@include flex-col-center;
		gap: 1rem;
		padding: 0 1.25rem 1.25rem;

		.quest-description {
			text-align: center;
		}
	}

	&__header {
		position: relative;
	}

	:deep(.box-header-bg) {
		position: relative;
		width: 100%;
		height: 4.625rem;

		.solid-bg-polygon {
			fill: rgba(145, 113, 56, 0.8) !important;
		}

		.texture-bg-polygon {
			opacity: 0.2 !important;
		}
	}

	&-reward {
		@include flex-row-center;
		margin-top: 0.75rem;

		&-value {
			margin-right: 0.25rem;
			margin-bottom: 0.25rem;
			font-size: 1rem;
		}

		&:not(:last-child) {
			margin-right: 0.75rem;
		}
	}

	&-description {
		text-align: center;
	}

	.claim-rewards-btn {
		position: relative;
		z-index: 1;
		margin: 0.75rem auto;
		width: 12.5rem;
		height: 2.25rem;
	}
}
</style>
